import React, { FormEvent, useState } from 'react';
import { HBox } from '../layout';
import { TextInput } from '../form';
import { Button, ValidIconName } from '../basic';
import { intl } from '../../lib';

/**
 * Props for a {@link ChatInput} component.
 * @author Tomas Lukac
 */
export interface ChatInputProps {
  /**
   * Icon that can be provided for a send button. Default is 'send_message'
   */
  icon?: ValidIconName;
  /**
   * If it's a multiline input or not.
   */
  multiline?: boolean;
  /**
   * Number of rows the textarea should take if multiline is true.
   */
  multilineRows?: number;
  /**
   * Handler for when chat message is submitted.
   * @param message message typed in the text input.
   */
  onMessageSubmit: (message: string) => void;
}

/**
 * Basic Chat input element.
 *
 * It consist of {@link TextInput} and {@link Button}. When submit is triggered, provided  onMessageSubmit function is fired.

 * @author Tomas Lukac
 * @constructor
 */
export function ChatInput({
  onMessageSubmit,
  icon = 'send_message',
  multiline = true,
  multilineRows = 2
}: ChatInputProps): JSX.Element {
  const [message, setMessage] = useState<string>('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onMessageSubmit(message);
    setMessage('');
  };

  return (
    <form className="ehs-chat-input" onSubmit={handleSubmit}>
      <HBox align="stretch">
        <TextInput
          className="chat-input-text"
          type="text"
          label={intl('common.messaging.input.label')}
          value={message}
          onChange={setMessage}
          multiline={multiline}
          multilineRows={multilineRows}
        />
        <Button
          className="chat-input-button"
          icon={icon}
          text={intl('common.send.message')}
          variant="tertiary"
          type="submit"
          disabled={message.trim() === ''}
          scale="extra-large"
        />
      </HBox>
    </form>
  );
}
